import Model, { attr, belongsTo, hasMany } from "@ember-data/model";
import { tKey } from "ember-intl";
import { task } from "ember-concurrency";
import type Project from "glesys-controlpanel/models/project";
import type InstanceDatabase from "glesys-controlpanel/models/instance-database";
import type InstanceUser from "glesys-controlpanel/models/instance-user";
import type { Type } from "@warp-drive/core-types/symbols";
import { service } from "@ember/service";
import type RouterService from "@ember/routing/router-service";
import { saveRecord } from "@warp-drive/legacy/compat/builders";

interface Plan {
  key: "string";
  cpucores: "integer";
  memoryingib: "integer";
  storageingib: "integer";
}

interface Pitr {
  status: "disabled" | "initializing" | "enabled";
  retentiondays: number;
  restoregranularityseconds: number;
  restorewindow: { earliest: string; latest: string } | null;
}

export default class DatabaseModel extends Model {
  @service declare router: RouterService;

  @attr declare fqdn: string;
  @attr declare name: string;
  @attr declare engine: "MYSQL" | "POSTGRESQL";
  @attr declare engineversion: string;

  @attr declare plankey: string;
  @attr declare datacenterkey: string;
  @attr declare maintenancewindow: { weekday: string; starttime: string; durationinminutes: number };

  @attr declare status: string;
  @attr declare plan: Plan;
  @attr declare allowlist: string[];
  @attr declare pitr?: Pitr | null;

  @belongsTo<Project>("project", { async: false, inverse: "databases" }) declare project: Project;

  @hasMany("instance-database", { async: false, inverse: "database" }) declare instanceDatabases: InstanceDatabase[];
  @hasMany("instance-user", { async: false, inverse: "database" }) declare instanceUsers: InstanceUser[];

  get normalizedEngineName(): "MySQL" | "PostgreSQL" | null {
    switch (this.engine) {
      case "MYSQL":
        return "MySQL";
      case "POSTGRESQL":
        return "PostgreSQL";
      default:
        return null;
    }
  }

  get isRunning(): boolean {
    return this.status?.toLowerCase() === "running";
  }

  get isLocked(): boolean {
    return this.status?.toLowerCase() === "locked";
  }

  get isCreating(): boolean {
    return this.status?.toLowerCase() === "creating";
  }

  get isUpdating(): boolean {
    return this.status?.toLowerCase() === "updating";
  }

  get statusIndicator(): { state: string; labelKey: string; showSpinner?: boolean } {
    switch (this.status?.toLowerCase()) {
      case "running":
        return { state: "online", labelKey: tKey("component.g-server-status.running") };
      case "creating":
        return { state: "busy", showSpinner: true, labelKey: tKey("component.g-server-status.creating") };
      case "updating":
        return { state: "busy", labelKey: tKey("component.g-server-status.updating") };
      case "locked":
        return { state: "busy", labelKey: tKey("component.g-server-status.locked") };
      case "failed":
        return { state: "error", labelKey: tKey("databases.details.status.failed") };
      default:
        return { state: "offline", labelKey: tKey("component.g-server-status.stopped") };
    }
  }

  get pitrStatusIndicator(): { state: string; labelKey: string } {
    return this.pitrIsEnabled
      ? { state: "online", labelKey: tKey("databases.details.pitr.status.enabled") }
      : { state: "busy", labelKey: tKey("databases.details.pitr.status.initializing") };
  }

  get pitrStatus(): string | undefined {
    return this.pitr?.status?.toLowerCase();
  }

  get pitrIsInitializing(): boolean {
    return this.pitrStatus === "initializing";
  }

  get pitrIsEnabled(): boolean {
    return this.pitrStatus === "enabled";
  }

  get hasPitr(): boolean {
    return this.pitrIsInitializing || this.pitrIsEnabled;
  }

  get monitoringUrl(): string {
    return `https://${this.fqdn}:9273/metrics`;
  }

  deleteTask = task(async () => {
    try {
      this.deleteRecord();
      await this.store.request(saveRecord(this));

      this.router.transitionTo("databases.overview");
    } catch (error) {
      this.rollbackAttributes();
      throw error;
    }
  });

  declare [Type]: "database";
}
